import { Crown } from 'lucide-react'

import type { SubscriptionTier } from '@/lib/api/schemas'
import { TIER_ACCENT, TIER_BRAND_LABEL } from '@/lib/squadron/tierMeta'
import { cn } from '@/lib/utils'

/**
 * Premium tier badge — used wherever the squadron's current tier needs
 * to feel like an earned status rather than another stat. Replaces the
 * plain text rendering inside KpiCard for the Tier slot, and shows up
 * in the header card / subscription card.
 *
 * Design rules applied (no decorative motion — this is rendered on every
 * dashboard visit, so per Emil's frequency framework we lean entirely on
 * static treatment):
 *  - Metallic radial gradient layered with a soft diagonal foil sheen
 *  - Tier accent on the border + a faint glow to feel embossed
 *  - Inset highlight strip at the top for a beveled, medal-like read
 *  - Tier-specific icon (crown for paid tiers, none for FREE)
 *
 * Sizing variants:
 *  - `lg` — used inside the dashboard KPI slot
 *  - `md` — header/subscription banners
 */
interface TierBadgeProps {
  tier: SubscriptionTier
  size?: 'md' | 'lg'
  className?: string
}

export function TierBadge({ tier, size = 'lg', className }: TierBadgeProps) {
  const accent = TIER_ACCENT[tier]
  const isPaid = tier !== 'FREE'
  const label = TIER_BRAND_LABEL[tier]

  const dims =
    size === 'lg'
      ? 'h-10 px-3.5 text-[13px] tracking-[0.18em]'
      : 'h-7 px-2.5 text-[11px] tracking-[0.15em]'

  return (
    <span
      className={cn(
        'font-display group relative inline-flex items-center gap-1.5 overflow-hidden rounded-md font-black uppercase select-none',
        dims,
        className,
      )}
      style={{
        // Layered backgrounds, bottom→top:
        //   1. A radial highlight from upper-left (depth)
        //   2. A subtle metallic vertical gradient (body)
        //   3. The tier-accent tint at low alpha (colour identity)
        backgroundImage: [
          // Foil sheen — a soft diagonal highlight that reads as polished
          // metal rather than flat paint. Kept low-opacity so it doesn't
          // wash the accent.
          `linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 35%, transparent 55%, rgba(255,255,255,0.08) 75%, transparent 100%)`,
          // Body — darker at the bottom, lighter at the top, in the tier
          // accent. Mimics the way a metal coin catches light.
          `linear-gradient(180deg, ${accent}3D 0%, ${accent}1A 60%, ${accent}29 100%)`,
        ].join(', '),
        borderColor: `${accent}99`,
        borderWidth: '1px',
        borderStyle: 'solid',
        color: accent,
        // The outer glow is a long, soft halo that picks up the accent.
        // Subtle on FREE (just a hint of depth) and brighter for paid tiers
        // so promotions feel rewarded.
        boxShadow: isPaid
          ? `0 0 0 1px ${accent}29 inset, 0 1px 0 ${accent}59 inset, 0 6px 22px -10px ${accent}A6`
          : `0 0 0 1px ${accent}1F inset, 0 1px 0 ${accent}29 inset`,
        textShadow: isPaid ? `0 1px 0 rgba(0,0,0,0.4)` : undefined,
      }}
    >
      {/* Top inset highlight — the "machined edge" of the medal. Sits as
          a 1px white strip just inside the border. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          backgroundImage:
            'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
        }}
      />
      {isPaid && <Crown className={size === 'lg' ? 'size-3.5' : 'size-3'} aria-hidden />}
      <span className="relative">{label}</span>
    </span>
  )
}
